import React, { FC, MouseEvent } from 'react';
import { addProtocol } from '../../../../utils/addProtocol';
import { TransitionType, makeTransition } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { IconForward } from '../../../icon/IconForward';
import { IconNewTab } from '../../../icon/IconNewTab';
import { IconProduct } from '../../../icon/IconProduct';
import { Button } from '../../../input/Button';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { ClickOut, PostUI } from '../../types';
import { PRODUCT_BUTTON_TRANSITION_TIMING } from '../AnimateFooter/constants';
import { ProductView } from './ProductView';
import {
    ProductContent,
    ProductContainer,
    MainButton,
    ButtonBox,
    ProductMinizeButton,
} from './styles';

interface PostButtonProps {
    clickOut: ClickOut | undefined;
    isExpanded: boolean;
    onClickOutClick: (event: MouseEvent<HTMLElement>) => void;
    products: PostUI['products'];
    isProductExpanded: boolean;
    transitionTiming: number;
    onProductButtonClick: VoidFunction;
    isRedesign?: boolean;
}

export const PostButton: FC<PostButtonProps> = ({
    onClickOutClick,
    isExpanded,
    clickOut,
    products,
    isProductExpanded,
    transitionTiming,
    onProductButtonClick,
}) => {
    const t = useTranslation();

    if (products.items.length) {
        return (
            <ButtonBox
                isExpanded={isExpanded}
                isProductExpanded={isProductExpanded}
                transition={makeTransition({
                    ms: transitionTiming,
                    styles: ['width', 'height'],
                    type:
                        transitionTiming === PRODUCT_BUTTON_TRANSITION_TIMING
                            ? TransitionType.EASE_OUT
                            : TransitionType.LINEAR,
                })}
            >
                <MainButton onClick={onProductButtonClick}>
                    <ProductMinizeButton
                        isProductExpanded={isProductExpanded}
                        transition={makeTransition({
                            ms: isProductExpanded ? 0 : transitionTiming,
                            styles: ['opacity'],
                            type: TransitionType.EASE_OUT,
                        })}
                    >
                        <Box mr="10px">
                            <IconProduct size={20} />
                        </Box>
                        {t(products.buttonTitle)}
                    </ProductMinizeButton>

                    <ProductContainer
                        isProductExpanded={isProductExpanded}
                        transition={makeTransition({
                            ms: isProductExpanded ? transitionTiming : 0,
                            styles: ['opacity'],
                            type: TransitionType.EASE_OUT,
                        })}
                    >
                        <ProductContent>
                            <ProductView products={products} />
                        </ProductContent>

                        <IconForward fontSize="16px" />
                    </ProductContainer>
                </MainButton>
            </ButtonBox>
        );
    }

    if (clickOut) {
        return (
            <ButtonBox
                isExpanded={isExpanded}
                dataTestId={DataTestId.CLICK_OUT}
                transition={makeTransition({
                    ms: transitionTiming,
                    styles: ['width', 'height'],
                })}
            >
                <Button
                    as="a"
                    target="_blank"
                    rel="nofollow noreferrer"
                    flexGrow={1}
                    href={addProtocol(clickOut.url)}
                    color="hardLight"
                    onClick={onClickOutClick}
                    width="100%"
                    fontSize="1rem"
                >
                    {clickOut.text}
                    <Box ml={1}>
                        <IconNewTab fontSize={14} color="#000" />
                    </Box>
                </Button>
            </ButtonBox>
        );
    }

    return null;
};
